import React from 'react';
import { useQuery } from '@apollo/client';
import { Alert, Badge, Button, Card, Spinner, Table } from 'react-bootstrap';
import { DEPLOYMENT_STATUS_QUERY, DeploymentStatusResult, ServiceDeployment } from './graphql';

/**
 * What every service in this environment is running right now.
 *
 * The environment is not a control on this screen: DNS_MAP points each environment at its
 * own admin host and the topbar dropdown keeps the path, so switching environment lands on
 * this same screen over there. The resolver reports the environment its own pod runs in.
 */

// A deploy that rolled pods lands both timestamps within a couple of minutes. An hour
// apart means the pipeline ran but Kubernetes had nothing to roll — the deploy succeeded
// and changed nothing, which is worth pointing at rather than leaving for someone to spot.
const UNCHANGED_THRESHOLD_MS = 60 * 60 * 1000;

const formatTime = (value: string | null) => (value ? new Date(value).toLocaleString() : '—');

function relativeAge(value: string | null): string {
  if (!value) {
    return '';
  }
  const ms = Date.now() - new Date(value).getTime();
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 48) {
    return `${hours}h ago`;
  }
  return `${Math.round(hours / 24)}d ago`;
}

function changedNothing(service: ServiceDeployment): boolean {
  if (!service.startedAt || !service.liveSince) {
    return false;
  }
  const gap = Math.abs(new Date(service.startedAt).getTime() - new Date(service.liveSince).getTime());
  return gap > UNCHANGED_THRESHOLD_MS;
}

const SourceBadge = ({ source }: { source: string | null }) => {
  if (!source) {
    return <Badge variant="light">source not stamped</Badge>;
  }
  return <Badge variant={source.toLowerCase() === 'gitlab' ? 'success' : 'warning'}>{source.toLowerCase()}</Badge>;
};

const Commit = ({ service }: { service: ServiceDeployment }) => {
  if (!service.commit) {
    return <span className="text-muted font-italic">not recorded</span>;
  }
  return (
    <>
      <code>{service.commit}</code>
      {service.branch && <div className="small text-muted">{service.branch}</div>}
    </>
  );
};

const Deployments = () => {
  const { data, loading, error, refetch } = useQuery<DeploymentStatusResult>(DEPLOYMENT_STATUS_QUERY, {
    fetchPolicy: 'network-only',
  });

  if (loading) {
    return (
      <div className="p-4">
        <Spinner animation="border" size="sm" /> <span className="ml-2">Reading the cluster…</span>
      </div>
    );
  }

  // The resolver returns an empty service list when it cannot reach the Kubernetes API,
  // which in practice means the Role/RoleBinding from k8s/admin/chart is not applied yet.
  // Say that, rather than showing an empty table that looks like nothing is deployed.
  if (error) {
    return (
      <div className="p-4">
        <Alert variant="danger">
          <Alert.Heading className="h6">Could not read the deployment status</Alert.Heading>
          <div className="small">{error.message}</div>
        </Alert>
      </div>
    );
  }

  const status = data?.deploymentStatus;
  const services = status?.services ?? [];

  return (
    <div className="p-4">
      <div className="d-flex align-items-baseline justify-content-between flex-wrap mb-3">
        <div>
          <h4 className="mb-1">Deployments</h4>
          <div className="small text-muted">
            {status ? (
              <>
                project <b>{status.project}</b> · namespace <b>{status.namespace}</b> · read{' '}
                {formatTime(status.fetchedAt)}
              </>
            ) : (
              'no data'
            )}
          </div>
        </div>
        <Button variant="outline-secondary" size="sm" onClick={() => refetch()}>
          Refresh
        </Button>
      </div>

      {services.length === 0 && (
        <Alert variant="warning" className="small">
          Nothing came back. Either this admin is running outside a cluster, or the deployment-reader Role in{' '}
          <code>k8s/admin/chart</code> has not been applied to this namespace yet — the server log will show which path
          the Kubernetes API refused.
        </Alert>
      )}

      {services.some(changedNothing) && (
        <Alert variant="warning" className="small">
          A service below started serving well before its last deploy ran. The deploy carried the same image, so
          Kubernetes rolled no pods — it succeeded and changed nothing.
        </Alert>
      )}

      <Card>
        <Table responsive hover size="sm" className="mb-0">
          <thead>
            <tr>
              <th>Service</th>
              <th>Running</th>
              <th>Deploy started</th>
              <th>Live since</th>
              <th>Built from</th>
            </tr>
          </thead>
          <tbody>
            {services.map((service) => (
              <tr key={service.name}>
                <td>
                  <b>{service.name}</b>
                  <div className="small text-muted">
                    {service.platform}
                    {service.workload ? ` · ${service.workload}` : ''}
                  </div>
                </td>
                <td>
                  <Commit service={service} />
                  {service.note && <div className="small text-muted font-italic">{service.note}</div>}
                </td>
                <td className="text-nowrap">{formatTime(service.startedAt)}</td>
                <td className="text-nowrap">
                  {formatTime(service.liveSince)}
                  <div className={`small ${changedNothing(service) ? 'text-warning' : 'text-muted'}`}>
                    {changedNothing(service) ? 'unchanged by the last deploy' : relativeAge(service.liveSince)}
                  </div>
                </td>
                <td>
                  <SourceBadge source={service.source} />
                  {service.build && <div className="small text-muted">build #{service.build}</div>}
                  {service.revision && <div className="small text-muted">rev {service.revision}</div>}
                  {service.ready && <div className="small text-muted">{service.ready} ready</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      <div className="small text-muted mt-2">
        Read from the Kubernetes and Cloud Run APIs, cached for 60 seconds. Switch environment from the topbar — it
        keeps this path and lands on the same screen in the other admin.
      </div>
    </div>
  );
};

export default Deployments;
